import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRealtimeEvent } from './RealtimeContext';
import { useUserId } from './UserContext';

interface SystemConfigContextType {
  execContext: any;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SystemConfigContext = createContext<SystemConfigContextType | undefined>(undefined);

// REQUEST DEDUPLICATION: Prevent parallel thundering herd on mount
// Moved outside component to survive React StrictMode double mounts
let globalFetchPromise: Promise<any> | null = null;

export const SystemConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [execContext, setExecContext] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const userId = useUserId();

  const fetchConfig = async () => {
    // If no userId, we can't fetch protected context (Rules #5 & #10 alignment)
    if (!userId) {
      setLoading(false);
      return;
    }

    // If a request is already in flight globally, await it
    if (globalFetchPromise) {
      try {
        const data = await globalFetchPromise;
        if (data) setExecContext(data);
      } finally {
        setLoading(false);
      }
      return globalFetchPromise;
    }

    setLoading(true);
    console.log('📡 [SystemConfig] Fetching global execution context (Consolidated)...');

    globalFetchPromise = (async () => {
      try {
        const { data, error } = await (supabase.rpc as any)('get_execution_context', { p_exchange: 'detect' });
        
        if (error) {
          console.error('❌ [SystemConfig] Failed to fetch:', error.message);
          return null;
        }

        if (data) {
          setExecContext(data);
          const typedData = data as any;
          console.log('✅ [SystemConfig] Context loaded:', {
            strategies: typedData.strategy_tier_config?.length,
            ai: typedData.ai_supervisor_enabled,
            exchange: typedData.detected_exchange || typedData.p_exchange_requested
          });
          return data;
        }
      } finally {
        // Clear the promise after completion (with a tiny cooldown to catch near-simultaneous calls)
        setTimeout(() => {
          globalFetchPromise = null;
        }, 1000);
        setLoading(false);
      }
    })();

    return globalFetchPromise;
  };

  useEffect(() => {
    fetchConfig();
  }, [userId]);

  // Realtime invalidation in ONE place (Rule #9 Compliance)
  useRealtimeEvent('strategy_tier_config', () => {
    console.log('🔄 [SystemConfig] Realtime update: strategy_tier_config');
    fetchConfig();
  });

  useRealtimeEvent('category_tier_config', () => {
    console.log('🔄 [SystemConfig] Realtime update: category_tier_config');
    fetchConfig();
  });

  useRealtimeEvent('system_config', () => {
    console.log('🔄 [SystemConfig] Realtime update: system_config');
    fetchConfig();
  });

  useRealtimeEvent('system_settings', () => {
    console.log('🔄 [SystemConfig] Realtime update: system_settings');
    fetchConfig();
  });

  return (
    <SystemConfigContext.Provider value={{ execContext, loading, refresh: fetchConfig }}>
      {children}
    </SystemConfigContext.Provider>
  );
};

export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext);
  if (context === undefined) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider');
  }
  return context;
};

